// 스킬 슬롯 저장(dungeonhero.skillslots.v1, docs/SKILL-TREE.md 11.4): 슬롯 배치 12칸 + 현재 페이지.
// 성장 v2 장부(dungeonhero.growth.v2)·profile.v2·UI 설정과 키를 나눠 기존 저장은 건드리지 않는다.
// 저장이 없으면 기본 배치(지금 습득한 스킬을 자동 배치 규칙대로 놓은 것 = 옛 U·I·O·L·P 순서)로 시작하므로 옛 저장은 이관 없이 열린다.
// 읽기 예외·미래 버전은 메모리 기본 배치로 쓰되 쓰지 않는다. 손상은 원문을 .bak에 한 번 남기고 기본 배치로 시작해 다음 flush 때 덮어쓴다.
// 저장은 고정 스텝 밖에서만 한다: 변경(페이지 전환·배치·자동 배치)은 메모리에 바로 반영하고 due 표시만 하며,
// 직렬화·localStorage 쓰기는 렌더 프레임·pagehide에서 부르는 flush()가 한 번에 합쳐 처리한다(PR #146 Codex: 틱 안 할당·동기 쓰기 금지).
import { EXTRA_SLOT_BASE, SLOT_COUNT, SLOT_TOTAL, SLOT_PAGES, SKILL_COUNT, SlotState, placedMask, slotsValid, type SlotCode } from "../shared/skillSlots";
import { learnedSkills } from "../shared/growth";
import { HERO_SKILL_TABLE, type SkillHeroId } from "../shared/skills/types";
import type { GrowthView } from "../shared/types";
import type { SaveStorage } from "./save";

export const SKILL_SLOTS_KEY = "dungeonhero.skillslots.v1";
/** 라이라 슬롯 배치(c022). 카엘 키(skillslots.v1)와 나눠 옛 빌드 롤백 때 카엘 배치가 손상되지 않게 한다. 형식은 같다(스킬 번호만 1..11) */
export const SKILL_SLOTS_LYRA_KEY = "dungeonhero.skillslots.lyra.v1";
export const SKILL_SLOTS_BACKUP_KEY = SKILL_SLOTS_KEY + ".bak";
export const SKILL_SLOTS_VERSION = 1;
/** 저장 실패 뒤 다시 쓸 차례를 만드는 간격(tick·sync 호출 횟수 = 렌더 프레임이 아니라 틱 단위 호출 기준 약 2초) */
const RETRY_CALLS = 120;

/**
 * 영웅별 '옛 빌드 상한'(스킬 번호). 옛 빌드의 slotsValid는 이 번호 넘는 칸을 손상(corrupt)으로 본다.
 * 그래서 이보다 큰 번호(라이라 전직 12·13)는 slots·x7에 0으로 쓰고 선택 필드 xs에 [칸, 스킬] 쌍으로 싣는다(옛 빌드는 xs를 모른 채 무시).
 * 이 표는 '출시된 옛 빌드'의 값이라 영웅의 스킬이 늘어도 올리지 않는다(올리면 옛 빌드 호환이 깨진다)
 */
export const LEGACY_SKILL_MAX: Readonly<Record<SkillHeroId, number>> = { kael: 10, lyra: 11 };

export type SlotsMode = "ok" | "future" | "unavailable";

type Parsed = { ok: true; page: number; slots: number[]; seen?: number; x7?: number[]; xs?: number[][]; xn?: number } | { ok: false; reason: "corrupt" | "future" };

/**
 * seen = 이미 자동 배치 대상으로 본 스킬 마스크(선택 필드). 놓인 스킬 마스크와 같으면 생략한다(옛 저장과 같은 모양 = 읽을 때 같은 값으로 해석).
 * 일부러 비운 칸이 새로고침 뒤 다시 채워지지 않게 "본 적 있는데 놓이지 않은" 스킬이 있을 때만 기록한다.
 */
export function serializeSlots(page: number, slots: ArrayLike<number>, seen?: number, count: number = SKILL_COUNT, legacyMax: number = count): string {
  // 기본 12칸만 slots에(옛 빌드 형식 그대로), 환생 추가 칸(레인 R)은 비어 있지 않을 때만 선택 필드 x7로
  // 옛 빌드 상한(legacyMax) 넘는 스킬 번호는 slots·x7에 0으로 쓰고 xs = [[칸, 스킬], …]로 따로 싣는다(옛 빌드는 xs를 모른다 = 손상 아님)
  const xs: number[][] = [];
  const at = (i: number): number => {
    const v = i < slots.length ? slots[i] | 0 : 0;
    if (v > legacyMax) { xs.push([i, v]); return 0; }
    return v;
  };
  const base: number[] = [];
  for (let i = 0; i < SLOT_COUNT; i++) base.push(at(i));
  const ex = [at(EXTRA_SLOT_BASE), at(EXTRA_SLOT_BASE + 1)];
  const o: Record<string, unknown> = { version: SKILL_SLOTS_VERSION, page, slots: base };
  if (seen !== undefined) {
    // seen의 옛 빌드 상한 위 비트는 xn으로 옮긴다(옛 빌드의 seen 범위 검사에 걸려 버려지지 않게)
    const lo = seen & ((1 << legacyMax) - 1), hi = seen >>> legacyMax;
    if (lo !== placedMask(slots, legacyMax) || hi !== 0) o.seen = lo;
    if (hi !== 0) o.xn = hi;
  }
  if (ex[0] !== 0 || ex[1] !== 0) o.x7 = ex;
  if (xs.length > 0) o.xs = xs;
  return JSON.stringify(o);
}
export function parseSlots(raw: string, count: number = SKILL_COUNT): Parsed {
  let v: unknown;
  try { v = JSON.parse(raw); } catch { return { ok: false, reason: "corrupt" }; }
  if (v === null || typeof v !== "object" || Array.isArray(v)) return { ok: false, reason: "corrupt" };
  const o = v as Record<string, unknown>;
  if (typeof o.version === "number" && o.version > SKILL_SLOTS_VERSION) return { ok: false, reason: "future" };
  if (o.version !== SKILL_SLOTS_VERSION) return { ok: false, reason: "corrupt" };
  if (!Number.isInteger(o.page) || (o.page as number) < 0 || (o.page as number) >= SLOT_PAGES) return { ok: false, reason: "corrupt" };
  if (!Array.isArray(o.slots) || !slotsValid(o.slots as number[], count)) return { ok: false, reason: "corrupt" };
  // seen은 선택 필드: 없거나 잘못된 값이면 옛 저장처럼 "놓인 스킬 = 본 스킬"로 읽는다
  const maxMask = (1 << count) - 1;
  // x7(환생 추가 칸)도 선택 필드: 잘못된 값이면 버린다(기본 12칸은 그대로 읽는다)
  // legacyMax 넘는 번호는 xs([칸, 스킬] 쌍)에만 있다: 쌍 하나가 잘못되면(범위 밖·중복) 그 쌍만 버리고 나머지는 그대로 읽는다
  const used = new Set<number>(), usedSlots = new Set<number>();
  for (let i = 0; i < SLOT_COUNT; i++) if ((o.slots as number[])[i] > 0) { used.add((o.slots as number[])[i]); usedSlots.add(i); }
  const x7 = Array.isArray(o.x7) && o.x7.length === SLOT_PAGES && o.x7.every((n) => Number.isInteger(n) && n >= 0 && n <= count) ? (o.x7 as number[]) : undefined;
  const out: Parsed = { ok: true, page: o.page as number, slots: o.slots as number[] };
  if (Number.isInteger(o.seen) && (o.seen as number) >= 0 && (o.seen as number) <= maxMask) out.seen = o.seen as number;
  if (x7) out.x7 = x7;
  if (Array.isArray(o.xs)) {
    const xs: number[][] = [];
    for (const pr of o.xs as unknown[]) {
      if (!Array.isArray(pr) || pr.length !== 2) continue;
      const [slot, skill] = pr as number[];
      if (!Number.isInteger(slot) || slot < 0 || slot >= SLOT_TOTAL || !Number.isInteger(skill) || skill < 1 || skill > count) continue;
      if (slot < SLOT_COUNT ? usedSlots.has(slot) : (x7 ? x7[slot - EXTRA_SLOT_BASE] !== 0 : false)) continue;
      if (used.has(skill) || (x7 && x7.includes(skill)) || xs.some((q) => q[0] === slot || q[1] === skill)) continue;
      xs.push([slot, skill]);
    }
    if (xs.length > 0) out.xs = xs;
  }
  if (Number.isInteger(o.xn) && (o.xn as number) > 0 && (o.xn as number) < 2 ** 8) out.xn = o.xn as number;
  return out;
}

export class SkillSlotStore extends SlotState {
  mode: SlotsMode = "ok";
  /** 마지막 저장 시도가 저장소에 기록됐는지(메모리 값은 유지) */
  lastSaveOk = true;
  /** 마지막 명령 결과(UI 문구용) */
  lastCode: SlotCode = "ok";
  /** 저장소에 아직 반영되지 않은 변경이 있다(쓰기 실패 뒤에도 true로 남는다) */
  private dirty = false;
  /** 다음 flush()가 쓸 차례다(변경 직후·재시도 시점에 켜지고, flush가 끈다) */
  private due = false;
  private retry = 0;
  private growthRev = -1;
  private growthLevel = -1;

  /** 이 배치의 저장 키·영웅(c022: 카엘 skillslots.v1, 라이라 skillslots.lyra.v1) */
  readonly key: string;
  readonly hero: SkillHeroId;

  constructor(private readonly storage: SaveStorage, options: { hero?: SkillHeroId } = {}) {
    const hero = options.hero ?? "kael";
    super(HERO_SKILL_TABLE[hero].count);
    this.hero = hero;
    this.key = hero === "kael" ? SKILL_SLOTS_KEY : SKILL_SLOTS_LYRA_KEY;
    this.read();
  }

  private read(): void {
    let raw: string | null;
    try { raw = this.storage.getItem(this.key); } catch { this.mode = "unavailable"; return; }
    if (raw === null) return;
    const p = parseSlots(raw, this.count);
    if (p.ok) { this.load(p.page, p.slots, p.seen === undefined && p.xn === undefined ? undefined : (p.seen ?? placedMask(p.slots, LEGACY_SKILL_MAX[this.hero])) | ((p.xn ?? 0) << LEGACY_SKILL_MAX[this.hero]), p.x7, p.xs); return; }
    if (p.reason === "future") { this.mode = "future"; return; }
    // 손상: 원문을 한 번 백업하고 기본 배치로 시작한다(다음 변경 때 덮어쓴다)
    const bak = this.key + ".bak";
    try { if (this.storage.getItem(bak) === null) this.storage.setItem(bak, raw); } catch { /* 백업 실패는 진행을 막지 않는다 */ }
    this.dirty = true;
    this.due = true; // 첫 flush가 손상 저장을 기본 배치로 덮어쓴다
  }

  /**
   * 성장 상태(레벨·랭크)가 바뀌었으면 습득 스킬을 자동 배치한다. 같은 revision·레벨이면 아무것도 하지 않는다(프레임마다 불려도 가볍다).
   * 저장은 하지 않는다(due 표시만): 실제 쓰기는 flush()가 한다. 저장 실패가 남았으면 RETRY_CALLS번째 호출마다 다시 쓸 차례를 만든다.
   */
  sync(g: Pick<GrowthView, "revision" | "level" | "ranks" | "kept">): void {
    if (g.revision !== this.growthRev || g.level !== this.growthLevel) {
      this.growthRev = g.revision;
      this.growthLevel = g.level;
      this.place(learnedSkills(g.level, g.ranks, this.hero, g.kept ?? 0));
    }
    this.tick();
  }

  /**
   * 틱마다 GameSim이 부른다: 저장 실패가 남았으면 RETRY_CALLS번째 틱마다 다시 쓸 차례(due)를 만든다(sync는 레벨업·성장 변경 때만 불려 재시도를 못 센다).
   * 직렬화·쓰기는 하지 않는다. 할당 없음
   */
  tick(): void {
    if (this.dirty && !this.due && ++this.retry >= RETRY_CALLS) { this.retry = 0; this.due = true; }
  }

  /**
   * 쓸 차례(due)이면 한 번 저장한다. 고정 스텝 밖(렌더 프레임·pagehide)에서 부른다. 그 사이 여러 번 바뀐 변경은 이 한 번의 쓰기로 합쳐진다.
   * 쓰기가 실패하면 dirty가 남아 tick()이 나중에 다시 due를 켠다.
   */
  flush(): void {
    if (this.due) { this.due = false; this.persist(); }
  }

  /** 명령: 슬롯 배치 바꾸기(마을에서만). denied는 GameSim이 정한 문맥 거절 */
  command(slot: number, skill: number, g: Pick<GrowthView, "level" | "ranks" | "kept">, denied?: SlotCode): SlotCode {
    const code = denied ?? this.assign(slot, skill, learnedSkills(g.level, g.ranks, this.hero, g.kept ?? 0));
    this.lastCode = code;
    this.revision++;
    return code;
  }

  /** 변경은 메모리에 이미 반영됐다. 저장은 다음 flush()로 미룬다(고정 스텝 안 직렬화·동기 쓰기 금지) */
  protected override changed(): void { this.dirty = true; this.due = true; this.retry = 0; }

  private persist(): void {
    if (this.mode !== "ok") return; // 미래 버전·읽기 불가 저장은 덮어쓰지 않는다
    try {
      this.storage.setItem(this.key, serializeSlots(this.page, this.all, this.seenMask, this.count, LEGACY_SKILL_MAX[this.hero]));
      this.lastSaveOk = true;
      this.dirty = false;
    } catch {
      this.lastSaveOk = false;
      this.dirty = true;
    }
  }
}
